'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { CheckCircle2, Loader2, MessageCircle, Send, X } from 'lucide-react';
import type { ContactStateResponse } from '@/lib/messages/types';
import { trackEvent } from '@/lib/analyticsEvents';

type Props = {
  open: boolean;
  targetName: string;
  targetHandle: string;
  onClose: () => void;
};

export default function ReachOutDialog({ open, targetName, targetHandle, onClose }: Props) {
  const router = useRouter();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const contactRequestRef = useRef(0);
  const openedConversationRef = useRef<string | null>(null);
  const [contact, setContact] = useState<ContactStateResponse | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [contactAttempt, setContactAttempt] = useState(0);
  const [contactError, setContactError] = useState('');
  const [actionError, setActionError] = useState('');

  const retryContactState = useCallback(() => {
    setLoading(true);
    setContactError('');
    setContact(null);
    setContactAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const requestId = ++contactRequestRef.current;
    const controller = new AbortController();
    openedConversationRef.current = null;
    setLoading(true);
    setContactError('');
    setActionError('');
    setContact(null);
    fetch(`/api/messages/contact-state?handle=${encodeURIComponent(targetHandle)}`, {
      signal: controller.signal,
      cache: 'no-store',
    })
      .then(async (response) => {
        const payload = (await response.json().catch(() => ({}))) as ContactStateResponse & {
          error?: string;
        };
        if (!response.ok) throw new Error(payload.error || 'Unable to start a conversation.');
        if (contactRequestRef.current === requestId) setContact(payload);
      })
      .catch((caught) => {
        if ((caught as Error).name !== 'AbortError') {
          if (contactRequestRef.current === requestId) {
            setContactError('Messages are temporarily unavailable. Please try again.');
          }
        }
      })
      .finally(() => {
        if (contactRequestRef.current === requestId) setLoading(false);
      });
    return () => controller.abort();
  }, [contactAttempt, open, targetHandle]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => closeRef.current?.focus(), 0);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), a[href], input:not([disabled])'
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus({ preventScroll: true });
      }
      previousFocusRef.current = null;
    };
  }, [onClose, open]);

  useEffect(() => {
    const conversationId = contact?.conversationId;
    if (
      !open ||
      contact?.state !== 'accepted' ||
      !conversationId ||
      openedConversationRef.current === conversationId
    ) {
      return;
    }
    openedConversationRef.current = conversationId;
    trackEvent('conversation_opened');
    router.push(`/messages?conversation=${encodeURIComponent(conversationId)}`);
    onClose();
  }, [contact, onClose, open, router]);

  if (!open) return null;

  const openConversation = () => {
    if (!contact?.conversationId) return;
    trackEvent('conversation_opened');
    router.push(`/messages?conversation=${encodeURIComponent(contact.conversationId)}`);
    onClose();
  };

  const sendRequest = async () => {
    const intro = message.trim();
    if (!intro || intro.length > 2000 || sending) return;
    setSending(true);
    setActionError('');
    try {
      const response = await fetch('/api/messages/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientHandle: targetHandle, message: intro }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        conversationId?: string;
        state?: ContactStateResponse['state'];
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || 'Your request could not be sent.');
      setContact({
        state: (payload.state || 'pending') as ContactStateResponse['state'],
        conversationId: payload.conversationId || null,
        requestState: 'pending',
      });
      setMessage('');
      trackEvent('message_request_sent');
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Your request could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const state = contact?.state;
  const handleLabel = `@${targetHandle.replace(/^@/, '')}`;

  return (
    <div className="fixed inset-0 z-[150] flex items-end justify-center p-0 sm:items-center sm:p-5">
      <button
        type="button"
        aria-label="Close message request"
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        data-clarity-mask="true"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reach-out-title"
        aria-describedby="reach-out-description"
        className="relative w-full max-w-lg rounded-t-2xl border border-slate-200 bg-white p-5 text-slate-950 shadow-[0_28px_90px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-slate-950 dark:text-slate-50 sm:rounded-2xl sm:p-6"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-[background-color,color,transform] duration-150 ease-out hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <X size={18} aria-hidden="true" />
        </button>

        <div className="pr-11">
          <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <MessageCircle size={20} aria-hidden="true" />
          </span>
          <h2 id="reach-out-title" className="text-xl font-bold text-slate-950 dark:text-white">
            Reach out to {targetName}
          </h2>
          <p id="reach-out-description" className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {handleLabel}. Your email address and private profile details are not shown to the
            recipient.
          </p>
        </div>

        {contactError || (loading && contactAttempt > 0) ? (
          <div
            role={contactError ? 'alert' : 'status'}
            aria-live={contactError ? 'assertive' : 'polite'}
            aria-busy={loading}
            className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200"
          >
            <p className="leading-6">{loading ? 'Checking message settings…' : contactError}</p>
            <button
              type="button"
              onClick={retryContactState}
              disabled={loading}
              className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-rose-300 bg-white px-4 font-bold text-rose-800 transition-[background-color,transform] duration-150 ease-out hover:bg-rose-100 disabled:cursor-wait disabled:opacity-60 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 dark:border-rose-400/30 dark:bg-slate-950 dark:text-rose-100 dark:hover:bg-rose-500/10"
            >
              {loading && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        ) : loading ? (
          <div
            role="status"
            aria-live="polite"
            className="flex min-h-40 items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400"
          >
            <Loader2 size={18} className="animate-spin" /> Checking message settings…
          </div>
        ) : state === 'accepted' ? (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/25 dark:bg-emerald-500/10">
            <p className="flex items-center gap-2 text-sm font-bold text-emerald-900 dark:text-emerald-200">
              <CheckCircle2 size={17} /> You already have a conversation.
            </p>
            <button
              type="button"
              onClick={openConversation}
              className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-teal-700 px-4 text-sm font-bold text-white transition-transform duration-150 ease-out active:scale-[0.98]"
            >
              Open conversation
            </button>
          </div>
        ) : state === 'pending' ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/25 dark:bg-amber-500/10">
            <p className="flex items-center gap-2 text-sm font-bold text-amber-950 dark:text-amber-200">
              <CheckCircle2 size={17} /> Message request sent
            </p>
            <p className="mt-1 text-sm leading-6 text-amber-900/80 dark:text-amber-100/80">
              You can send more messages after {targetName} accepts.
            </p>
            {contact?.conversationId && (
              <button
                type="button"
                onClick={openConversation}
                className="mt-4 min-h-11 rounded-xl border border-amber-300 bg-white px-4 text-sm font-bold text-amber-950 dark:border-amber-400/30 dark:bg-slate-900 dark:text-amber-100"
              >
                View request
              </button>
            )}
          </div>
        ) : state === 'restricted' ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Messaging is limited by this member’s privacy settings.
          </div>
        ) : state === 'declined' || state === 'unavailable' || state === 'self' ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            You cannot message this member.
          </div>
        ) : state === 'can_request' ? (
          <div className="mt-6">
            <label
              htmlFor="reach-out-message"
              className="text-sm font-bold text-slate-800 dark:text-slate-100"
            >
              Introductory message
            </label>
            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Send one thoughtful introduction. You can continue only after it is accepted.
            </p>
            <textarea
              id="reach-out-message"
              value={message}
              onChange={(event) => setMessage(event.target.value.slice(0, 2000))}
              rows={5}
              maxLength={2000}
              placeholder="Introduce yourself and say why you’re reaching out…"
              className="mt-3 w-full resize-y rounded-xl border border-slate-300 bg-white px-3.5 py-3 text-sm leading-6 text-slate-950 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:border-white/15 dark:bg-slate-900 dark:text-white dark:focus:ring-teal-500/20"
            />
            <div className="mt-1 flex justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span>Plain text only</span>
              <span>{message.length}/2,000</span>
            </div>
            <button
              type="button"
              onClick={() => void sendRequest()}
              disabled={sending || !message.trim()}
              className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 text-sm font-bold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-55 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? 'Sending…' : 'Send message request'}
            </button>
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Messaging is temporarily unavailable. Please try again.
          </div>
        )}

        {actionError && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200"
          >
            {actionError}
          </p>
        )}
      </div>
    </div>
  );
}
